import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { STORIES } from "../data/stories";
import { API_BASE } from "../config";
import AuthModal from "../components/AuthModal";

// ── Spritz pivot helper (matches SpeedReader.jsx) ──────────────────────────
function getPivotIndex(word) {
  const clean = word.replace(/[^a-zA-Z0-9]/g, "");
  const len = clean.length;
  if (len <= 1) return 0;
  if (len <= 5) return 1;
  if (len <= 9) return 2;
  if (len <= 13) return 3;
  return 4;
}

// ── Demo text ──────────────────────────────────────────────────────────────
const DEMO_TEXT =
  "Can you keep up? Each word appears exactly where your eye already rests so you never have to scan across a line, even documents that were once hard to read on your phone become completely effortless. Upload any document on your computer and it saves to the cloud automatically so you can stay productive and keep reading even when you are away from your desk. Ready to try it with your own PDF?";

// Mirrors SpeedReader's delay logic so the demo feels identical to the real reader
function getDelayMultiplier(word) {
  if (/[.…]{3,}['")\]]*$/.test(word) || /…['")\]]*$/.test(word)) return 2.2;
  if (/[.!?]['")\]]*$/.test(word)) return 1.8;
  if (/—$/.test(word)) return 1.5;
  if (/:['")\]]*$/.test(word)) return 1.4;
  if (/[,;]['")\]]*$/.test(word)) return 1.25;
  if (/-/.test(word)) {
    const parts = word.split("-").filter(Boolean);
    return Math.min(1.0 + (parts.length - 1) * 0.2, 2.0);
  }
  const clean = word.replace(/[^a-zA-Z0-9]/g, "");
  if (clean.length > 8) return Math.min(1.0 + (clean.length - 8) * 0.05, 1.5);
  return 1.0;
}

const DEMO_WORDS = DEMO_TEXT.split(/\s+/).filter(Boolean).map((word) => ({
  word,
  multiplier: getDelayMultiplier(word),
}));

function getWpmForIndex(idx) {
  if (idx <= 2)  return 150;
  if (idx <= 6)  return 160;
  if (idx <= 14) return 220;
  if (idx <= 24) return 280;
  return 300;
}

// ── Main OnboardingPage ────────────────────────────────────────────────────
export default function OnboardingPage({ onEnterReader }) {
  const { isAuthConfigured } = useAuth();
  const [demoIndex, setDemoIndex] = useState(0);
  const [demoDone, setDemoDone] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'login' | 'signup' | null
  const [loadingStoryId, setLoadingStoryId] = useState(null);
  const [storyError, setStoryError] = useState("");
  const fileInputRef = useRef(null);
  const timeoutRef = useRef(null);
  // ── Auto-play demo with speed ramp ──────────────────────────────────────
  // scheduleNext stored in a ref so the recursive callback always has the
  // latest version and avoids stale-closure / StrictMode double-fire issues.
  const scheduleNextRef = useRef(null);
  scheduleNextRef.current = (idx) => {
    if (idx >= DEMO_WORDS.length) {
      setDemoDone(true);
      return;
    }
    const wpm = getWpmForIndex(idx);
    const multiplier = DEMO_WORDS[idx].multiplier;
    const baseDelay = Math.round(60000 / wpm);
    const delay = Math.min(Math.round(baseDelay * multiplier), 700);
    timeoutRef.current = setTimeout(() => {
      setDemoIndex(idx);
      scheduleNextRef.current(idx + 1);
    }, delay);
  };

  useEffect(() => {
    // Initial pause so the page renders before words start flying
    const startTimer = setTimeout(() => scheduleNextRef.current(0), 300);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(timeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Story selection ──────────────────────────────────────────────────────
  const handleSelectStory = useCallback(async (story) => {
    setStoryError("");
    setLoadingStoryId(story.id);
    try {
      // Fetch text and PDF in parallel
      const [textRes, pdfRes] = await Promise.all([
        fetch(`${API_BASE}/api/stories/${story.id}`),
        fetch(`${API_BASE}/api/stories/${story.id}/pdf`),
      ]);
      if (!textRes.ok) throw new Error("Could not load story");
      const data = await textRes.json();
      const blobUrl = pdfRes.ok
        ? URL.createObjectURL(await pdfRes.blob())
        : null;
      onEnterReader({
        text: data.text,
        filename: `${data.title} — ${data.author}`,
        blobUrl,
        fileId: data.file_id || null,
        pageBreaks: data.pageBreaks || [],
        pageWordCounts: data.page_word_counts || [],
      });
    } catch (err) {
      setStoryError("Failed to load story. Check that the backend is running.");
    } finally {
      setLoadingStoryId(null);
    }
  }, [onEnterReader]);

  // ── PDF upload from onboarding ───────────────────────────────────────────
  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const blobUrl = URL.createObjectURL(file);
    // Pass file to App.js for upload + text extraction
    onEnterReader({ file, blobUrl, filename: file.name, text: "" });
  }, [onEnterReader]);

  // ── Focal word rendering ─────────────────────────────────────────────────
  const currentWord = DEMO_WORDS[demoIndex]?.word || "";
  const pivotIdx = getPivotIndex(currentWord);
  const before = currentWord.slice(0, pivotIdx);
  const pivot = currentWord[pivotIdx] || "";
  const after = currentWord.slice(pivotIdx + 1);

  return (
    <div className="ob-page">
      {/* Header */}
      <header className="ob-header">
        <span className="ob-logo">Flash Focus</span>
        {isAuthConfigured && (
          <div className="ob-header-actions">
            <button className="ob-btn-ghost" onClick={() => setModalMode("login")}>Sign in</button>
            <button className="ob-btn-primary" onClick={() => setModalMode("signup")}>Sign up</button>
          </div>
        )}
      </header>

      <main className="ob-main">
        {/* Demo reader section */}
        <section className={`ob-demo-section${demoDone ? " ob-demo-section--done" : ""}`}>
          <div className="ob-focal-wrap">
            <div className="ob-focal-bar ob-focal-bar-top" />
            <div className="speed-reader-focal ob-focal">
              <div className="speed-reader-word">
                <span className="speed-reader-before">{before}</span>
                <span className="speed-reader-pivot">{pivot}</span>
                <span className="speed-reader-after">{after}</span>
              </div>
            </div>
            <div className="ob-focal-bar ob-focal-bar-bottom" />
          </div>
          {!demoDone && (
            <button
              className="ob-skip-btn"
              onClick={() => {
                clearTimeout(timeoutRef.current);
                setDemoDone(true);
              }}
            >
              Skip intro ↓
            </button>
          )}
        </section>

        {/* Story selection — fades in after demo finishes */}
        <section className={`ob-stories-section${demoDone ? " ob-fade-in" : " ob-hidden"}`}>
          <button
            className="ob-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={loadingStoryId !== null}
          >
            Upload your own PDF
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <div className="ob-divider"><span>or try a sample</span></div>

          <h2 className="ob-stories-heading">Try reading one of these</h2>
          <div className="ob-story-cards">
            {STORIES.map((story) => (
              <button
                key={story.id}
                className="ob-story-card"
                onClick={() => handleSelectStory(story)}
                disabled={loadingStoryId !== null}
              >
                <span className="ob-story-emoji">{story.emoji}</span>
                <span className="ob-story-title">{story.title}</span>
                <span className="ob-story-author">{story.author}</span>
                <span className="ob-story-desc">{story.description}</span>
                {loadingStoryId === story.id && <span className="ob-story-loading">Loading…</span>}
              </button>
            ))}
          </div>

          {storyError && <p className="ob-story-error">{storyError}</p>}
        </section>
      </main>

      {/* Auth modal */}
      {modalMode && <AuthModal onClose={() => setModalMode(null)} />}
    </div>
  );
}
